import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { toast, ToastOptions } from 'react-toastify';
import { noAutoClose } from 'assets/data/config';
import parseErrorMessage from 'utils/parseErrorMessage';
import { useSelector } from 'react-redux';

interface RootState {
  app: {
    user: {
      user_guid: string;
    };
  };
}

interface IncidentMember {
  user_guid: string;
  incident_member_id?: string;
  [key: string]: any;
}

interface AddUserToIncidentParams {
  incidentMembers: IncidentMember[];
  memberEmails: string[];
}

export interface TeamMembership {
  name: string;
}

export interface ProfileSettings {
  is_new_user: string;
  userName: string;
  mobile_phone_number: string;
  home_phone_number: string;
  pager_address: string;
  opt_in_for_sms: boolean;
}

export interface IncidentRosterMember {
  incident_member_id: string;
  incident_id: string;
  user_guid: string;
  role: string;
  active: number;
  exited: number;
  seat: string | null;
  incident_seat: string;
  active_time: string | null;
  minutes_worked: string;
  seat_name: string | null;
  seat_description: string | null;
  email_address: string;
  profile_settings: ProfileSettings;
  user_name: string;
  allow_admin_profile_edits: boolean;
  relevant_team_memberships: TeamMembership[];
}

export interface RosterItem {
  // from User
  user_guid: string;
  email_address: string;
  subscription_id: string;
  currently_selected: any;
  profile_settings: ProfileSettings;
  user_name: string;

  // from Group
  group_guid: string;
  group_name: string;
  is_closed: boolean | null;
  default_settings: any;
  default_sender_email: null | string;
  risk_levels: string[];
  daily_digest_team: string;
  allow_admin_profile_edits: boolean;

  role_assignments: { name: string }[];
  unavailability: any[];
  seats: any[];
}

// Helper function for error handling
const handleError = (error: any, errorMessage: string): void => {
  const fullErrorMessage = `${errorMessage}\n\n${parseErrorMessage(error)}\n\nIf you continue to experience this error, contact support@disastertech.com`;
  console.error(fullErrorMessage);
  toast.error(fullErrorMessage, noAutoClose as ToastOptions);
};

export const useGetRosterForIncident = (
  incidentId: string
): UseQueryResult<IncidentRosterMember[], Error> => {
  return useQuery<IncidentRosterMember[], Error>({
    queryKey: ['incidentRoster', incidentId],
    queryFn: async () => {
      if (!incidentId) return [];
      const response = await axios(config.apiGateway.getRosterForIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
        },
      });
      return response.data || [];
    },
    enabled: !!incidentId,
  });
};

export const useGetRostersForGroup = (
  groupGuid: string
): UseQueryResult<RosterItem[], Error> => {
  const { user_guid } = useSelector((state: RootState) => state.app.user);

  return useQuery<RosterItem[], Error>({
    queryKey: ['groupRosters', groupGuid],
    queryFn: async () => {
      if (!groupGuid) return [];
      const response = await axios(config.apiGateway.fetchRostersForGroups, {
        method: 'POST',
        data: {
          group_guids: [groupGuid],
          user_guid: user_guid,
        },
      });
      return response.data || [];
    },
    enabled: !!groupGuid && !!user_guid,
  });
};

export const useAddUserToIncident = (incidentId: string, groupGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ incidentMembers }: AddUserToIncidentParams) => {
      const response = await axios(config.apiGateway.addUserToIncident, {
        method: 'POST',
        data: {
          incident_id: incidentId,
          incidentMembers,
          group_guid: groupGuid,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', incidentId],
      });
      toast.success('User added to incident successfully');
    },
    onError: (error: any) => {
      if (error.response?.data === '{"ERROR":"User does not exist"}') {
        toast.error('User does not exist');
      } else if (
        error.response?.data === '{"ERROR":"User already in this Incident"}'
      ) {
        toast.error('User already in this Incident');
      } else {
        handleError(error, 'ERROR adding incident member');
      }
    },
  });
};
